import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    DatePicker,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dropdown,
    IColumn,
    IDropdownOption,
    IGroup,
    IconButton,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';
import {
    downloadSrxBundle,
    listSrxBundleFacets,
    listSrxBundles,
} from '../../../../AGSS/api/AGSSApi';
import {
    SrxBundleListFilter,
    SrxBundleListItem,
} from '../../../../AGSS/models/SrxBundle';

interface ISrxBundlesProps {}

const anyOption: IDropdownOption = { key: '', text: '(any)' };

const toOptions = (values: string[]): IDropdownOption[] => [
    anyOption,
    ...values.map((v) => ({ key: v, text: v })),
];

const formatBytes = (bytes: number | null | undefined): string => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024)
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatCreatedOn = (value: string | null | undefined): string => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

// Extracts the folder path (between the container name and the bundle file)
// from a blob URL. Falls back to '(root)' if the URL does not match the
// expected shape so misformatted entries still render.
const getFolderPath = (url: string): string => {
    if (!url) return '(root)';
    try {
        const path = new URL(url).pathname;
        const segments = path.split('/').filter((s) => s.length > 0);
        // segments[0] is the container name; the last segment is the file.
        const folderSegments = segments.slice(1, -1);
        return folderSegments.length === 0
            ? '(root)'
            : folderSegments.join('/');
    } catch {
        return '(root)';
    }
};

const buildGroupedItems = (
    items: SrxBundleListItem[]
): { sorted: SrxBundleListItem[]; groups: IGroup[] } => {
    const sorted = [...items].sort((a, b) => {
        const pa = getFolderPath(a.url);
        const pb = getFolderPath(b.url);
        if (pa !== pb) return pa.localeCompare(pb);
        return (a.bundleId ?? '').localeCompare(b.bundleId ?? '');
    });

    const groups: IGroup[] = [];
    let current: IGroup | null = null;
    sorted.forEach((item, idx) => {
        const folder = getFolderPath(item.url);
        if (current === null || current.key !== folder) {
            current = {
                key: folder,
                name: folder,
                startIndex: idx,
                count: 1,
            };
            groups.push(current);
        } else {
            current.count += 1;
        }
    });

    return { sorted, groups };
};

export const SrxBundles: React.FC<ISrxBundlesProps> = () => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const stackTokens = { childrenGap: 16 };

    const [outcome, setOutcome] = useState<string>('');
    const [deploymentType, setDeploymentType] = useState<string>('');
    const [pipelineKind, setPipelineKind] = useState<string>('');
    const [artifactSource, setArtifactSource] = useState<string>('');
    const [createdAfter, setCreatedAfter] = useState<Date | undefined>();
    const [createdBefore, setCreatedBefore] = useState<Date | undefined>();
    const [top, setTop] = useState<string>('50');

    // Facet options pulled live from the SRX bundle container. Each level loads
    // when its parent is selected; clearing a parent collapses its children.
    const [outcomeOptions, setOutcomeOptions] = useState<IDropdownOption[]>([
        anyOption,
    ]);
    const [deploymentTypeOptions, setDeploymentTypeOptions] = useState<
        IDropdownOption[]
    >([anyOption]);
    const [pipelineKindOptions, setPipelineKindOptions] = useState<
        IDropdownOption[]
    >([anyOption]);
    const [artifactSourceOptions, setArtifactSourceOptions] = useState<
        IDropdownOption[]
    >([anyOption]);

    const [facetsLoading, setFacetsLoading] = useState<
        Partial<
            Record<
                | 'outcome'
                | 'deploymentType'
                | 'pipelineKind'
                | 'artifactSource',
                boolean
            >
        >
    >({});
    const [facetsError, setFacetsError] = useState<string | undefined>();

    const loadFacet = useCallback(
        async (
            level:
                | 'outcome'
                | 'deploymentType'
                | 'pipelineKind'
                | 'artifactSource',
            parent: {
                outcome?: string;
                deploymentType?: string;
                pipelineKind?: string;
            }
        ) => {
            setFacetsLoading((prev) => ({ ...prev, [level]: true }));
            setFacetsError(undefined);
            try {
                const values = await listSrxBundleFacets(parent);
                const opts = toOptions(values);
                if (level === 'outcome') setOutcomeOptions(opts);
                else if (level === 'deploymentType')
                    setDeploymentTypeOptions(opts);
                else if (level === 'pipelineKind') setPipelineKindOptions(opts);
                else setArtifactSourceOptions(opts);
            } catch (err: any) {
                setFacetsError(
                    err?.response?.data?.error ??
                        err?.response?.data?.message ??
                        err?.message ??
                        'Failed to load facet values'
                );
            } finally {
                setFacetsLoading((prev) => ({ ...prev, [level]: false }));
            }
        },
        []
    );

    // Load outcomes on mount.
    useEffect(() => {
        loadFacet('outcome', {});
    }, [loadFacet]);

    const [items, setItems] = useState<SrxBundleListItem[]>([]);
    const [continuationToken, setContinuationToken] = useState<string | null>(
        null
    );
    const [listing, setListing] = useState<boolean>(false);
    const [listError, setListError] = useState<string | undefined>();

    const [directUrl, setDirectUrl] = useState<string>('');
    const [downloadingUrl, setDownloadingUrl] = useState<string | undefined>();
    const [downloadError, setDownloadError] = useState<string | undefined>();

    const buildFilter = useCallback(
        (token?: string): SrxBundleListFilter => {
            const parsedTop = parseInt(top, 10);
            // Treat the picker dates as inclusive whole-day bounds in local time:
            // start-of-day for the lower bound, end-of-day for the upper.
            const after = createdAfter
                ? new Date(
                      createdAfter.getFullYear(),
                      createdAfter.getMonth(),
                      createdAfter.getDate(),
                      0,
                      0,
                      0,
                      0
                  ).toISOString()
                : undefined;
            const before = createdBefore
                ? new Date(
                      createdBefore.getFullYear(),
                      createdBefore.getMonth(),
                      createdBefore.getDate(),
                      23,
                      59,
                      59,
                      999
                  ).toISOString()
                : undefined;
            return {
                outcome: outcome || undefined,
                deploymentType: deploymentType.trim() || undefined,
                pipelineKind: pipelineKind.trim() || undefined,
                artifactSource: artifactSource.trim() || undefined,
                top:
                    Number.isFinite(parsedTop) && parsedTop > 0
                        ? parsedTop
                        : undefined,
                continuationToken: token,
                createdAfter: after,
                createdBefore: before,
            };
        },
        [
            outcome,
            deploymentType,
            pipelineKind,
            artifactSource,
            top,
            createdAfter,
            createdBefore,
        ]
    );

    const handleList = useCallback(
        async (append: boolean = false) => {
            setListing(true);
            setListError(undefined);
            try {
                const result = await listSrxBundles(
                    buildFilter(
                        append ? continuationToken ?? undefined : undefined
                    )
                );
                setItems((prev) =>
                    append ? [...prev, ...result.items] : result.items
                );
                setContinuationToken(result.continuationToken ?? null);
            } catch (err: any) {
                setListError(
                    err?.response?.data?.error ??
                        err?.response?.data?.message ??
                        err?.message ??
                        'Failed to list bundles'
                );
                if (!append) setItems([]);
            } finally {
                setListing(false);
            }
        },
        [buildFilter, continuationToken]
    );

    const { sorted: groupedItems, groups } = useMemo(
        () => buildGroupedItems(items),
        [items]
    );

    const handleDownload = useCallback(async (bundleUrl: string) => {
        if (!bundleUrl) return;
        setDownloadingUrl(bundleUrl);
        setDownloadError(undefined);
        try {
            const ok = await downloadSrxBundle(bundleUrl);
            if (!ok) {
                setDownloadError(
                    'Bundle download failed. Check the URL and your permissions.'
                );
            }
        } finally {
            setDownloadingUrl(undefined);
        }
    }, []);

    const columns: IColumn[] = [
        {
            key: 'bundleId',
            name: 'Bundle ID',
            fieldName: 'bundleId',
            minWidth: 220,
            maxWidth: 320,
            isResizable: true,
            onRender: (item: SrxBundleListItem) => (
                <Text variant="small">{item.bundleId}</Text>
            ),
        },
        {
            key: 'createdOn',
            name: 'Created',
            fieldName: 'createdOn',
            minWidth: 140,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: SrxBundleListItem) => (
                <Text variant="small">{formatCreatedOn(item.createdOn)}</Text>
            ),
        },
        {
            key: 'sizeBytes',
            name: 'Size',
            fieldName: 'sizeBytes',
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            onRender: (item: SrxBundleListItem) => (
                <Text variant="small">{formatBytes(item.sizeBytes)}</Text>
            ),
        },
        {
            key: 'download',
            name: '',
            minWidth: 40,
            maxWidth: 40,
            onRender: (item: SrxBundleListItem) => (
                <IconButton
                    iconProps={{ iconName: 'Download' }}
                    title="Download bundle"
                    ariaLabel="Download bundle"
                    onClick={() => handleDownload(item.url)}
                    disabled={!!downloadingUrl}
                />
            ),
        },
    ];

    const panelStyle: React.CSSProperties = {
        padding: '12px',
        backgroundColor: theme.palette.white,
        borderRadius: '4px',
        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
    };

    return (
        <Stack tokens={stackTokens}>
            <MessageBar messageBarType={MessageBarType.info}>
                Browse SRX state bundles produced by Sovereign Release Extension
                runs. Use the filters to narrow by outcome bucket, or paste a
                bundle URL to download it directly.
            </MessageBar>

            {/* Direct download */}
            <div style={panelStyle}>
                <Stack tokens={{ childrenGap: 8 }}>
                    <Text
                        variant="mediumPlus"
                        styles={{ root: { fontWeight: 600 } }}
                    >
                        Download by URL
                    </Text>
                    <TextField
                        label="Bundle URL"
                        placeholder="https://<account>.blob.core.windows.net/srx-bundles/.../{bundleId}.zip"
                        value={directUrl}
                        onChange={(_, v) => setDirectUrl(v ?? '')}
                        borderless
                    />
                    <Stack horizontal tokens={{ childrenGap: 8 }}>
                        <PrimaryButton
                            text="Download"
                            iconProps={{ iconName: 'Download' }}
                            onClick={() => handleDownload(directUrl.trim())}
                            disabled={!directUrl.trim() || !!downloadingUrl}
                        />
                        {downloadingUrl === directUrl.trim() && (
                            <Spinner size={SpinnerSize.small} />
                        )}
                    </Stack>
                </Stack>
            </div>

            {/* Browse */}
            <div style={panelStyle}>
                <Stack tokens={{ childrenGap: 8 }}>
                    <Text
                        variant="mediumPlus"
                        styles={{ root: { fontWeight: 600 } }}
                    >
                        Browse
                    </Text>
                    <Text
                        variant="small"
                        styles={{
                            root: { color: theme.palette.neutralSecondary },
                        }}
                    >
                        Filters form a path prefix. Each filter requires the one
                        above it (Deployment Type needs Outcome, Pipeline Kind
                        needs Deployment Type, Artifact Source needs Pipeline
                        Kind).
                    </Text>
                    <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
                        <Dropdown
                            label="Outcome"
                            options={outcomeOptions}
                            selectedKey={outcome}
                            onChange={(_, opt) => {
                                const next = (opt?.key as string) ?? '';
                                setOutcome(next);
                                setDeploymentType('');
                                setPipelineKind('');
                                setArtifactSource('');
                                setDeploymentTypeOptions([anyOption]);
                                setPipelineKindOptions([anyOption]);
                                setArtifactSourceOptions([anyOption]);
                                if (next) {
                                    loadFacet('deploymentType', {
                                        outcome: next,
                                    });
                                }
                            }}
                            disabled={!!facetsLoading.outcome}
                            styles={{ root: { minWidth: 140 } }}
                        />
                        <Dropdown
                            label="Deployment Type"
                            options={deploymentTypeOptions}
                            selectedKey={deploymentType}
                            onChange={(_, opt) => {
                                const next = (opt?.key as string) ?? '';
                                setDeploymentType(next);
                                setPipelineKind('');
                                setArtifactSource('');
                                setPipelineKindOptions([anyOption]);
                                setArtifactSourceOptions([anyOption]);
                                if (next) {
                                    loadFacet('pipelineKind', {
                                        outcome,
                                        deploymentType: next,
                                    });
                                }
                            }}
                            disabled={
                                !outcome || !!facetsLoading.deploymentType
                            }
                            styles={{ root: { minWidth: 200 } }}
                        />
                        <Dropdown
                            label="Pipeline Kind"
                            options={pipelineKindOptions}
                            selectedKey={pipelineKind}
                            onChange={(_, opt) => {
                                const next = (opt?.key as string) ?? '';
                                setPipelineKind(next);
                                setArtifactSource('');
                                setArtifactSourceOptions([anyOption]);
                                if (next) {
                                    loadFacet('artifactSource', {
                                        outcome,
                                        deploymentType,
                                        pipelineKind: next,
                                    });
                                }
                            }}
                            disabled={
                                !deploymentType || !!facetsLoading.pipelineKind
                            }
                            styles={{ root: { minWidth: 140 } }}
                        />
                        <Dropdown
                            label="Artifact Source"
                            options={artifactSourceOptions}
                            selectedKey={artifactSource}
                            onChange={(_, opt) =>
                                setArtifactSource((opt?.key as string) ?? '')
                            }
                            disabled={
                                !pipelineKind || !!facetsLoading.artifactSource
                            }
                            styles={{ root: { minWidth: 160 } }}
                        />
                        <TextField
                            label="Top"
                            value={top}
                            onChange={(_, v) => setTop(v ?? '')}
                            styles={{ root: { width: 80 } }}
                        />
                    </Stack>
                    <Stack
                        horizontal
                        tokens={{ childrenGap: 12 }}
                        wrap
                        verticalAlign="end"
                    >
                        <DatePicker
                            label="Created after"
                            placeholder="Select a date..."
                            value={createdAfter}
                            onSelectDate={(d) =>
                                setCreatedAfter(d ?? undefined)
                            }
                            maxDate={createdBefore}
                            allowTextInput
                            styles={{ root: { minWidth: 180 } }}
                        />
                        <DatePicker
                            label="Created before"
                            placeholder="Select a date..."
                            value={createdBefore}
                            onSelectDate={(d) =>
                                setCreatedBefore(d ?? undefined)
                            }
                            minDate={createdAfter}
                            allowTextInput
                            styles={{ root: { minWidth: 180 } }}
                        />
                        {(createdAfter || createdBefore) && (
                            <DefaultButton
                                text="Clear dates"
                                onClick={() => {
                                    setCreatedAfter(undefined);
                                    setCreatedBefore(undefined);
                                }}
                            />
                        )}
                    </Stack>
                    {facetsError && (
                        <MessageBar messageBarType={MessageBarType.warning}>
                            {facetsError}
                        </MessageBar>
                    )}
                    <Stack horizontal tokens={{ childrenGap: 8 }}>
                        <PrimaryButton
                            text="List"
                            iconProps={{ iconName: 'Search' }}
                            onClick={() => handleList(false)}
                            disabled={listing}
                        />
                        {listing && <Spinner size={SpinnerSize.small} />}
                    </Stack>
                </Stack>
            </div>

            {listError && (
                <MessageBar messageBarType={MessageBarType.error}>
                    {listError}
                </MessageBar>
            )}
            {downloadError && (
                <MessageBar messageBarType={MessageBarType.error}>
                    {downloadError}
                </MessageBar>
            )}

            {items.length > 0 && (
                <div style={panelStyle}>
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Text
                            variant="medium"
                            styles={{ root: { fontWeight: 600 } }}
                        >
                            Results ({items.length}
                            {continuationToken ? '+' : ''})
                        </Text>
                        <DetailsList
                            items={groupedItems}
                            groups={groups}
                            columns={columns}
                            selectionMode={SelectionMode.none}
                            layoutMode={DetailsListLayoutMode.justified}
                            groupProps={{ showEmptyGroups: false }}
                            compact
                        />
                        {continuationToken && (
                            <Stack horizontal tokens={{ childrenGap: 8 }}>
                                <DefaultButton
                                    text="Load more"
                                    iconProps={{ iconName: 'ChevronDown' }}
                                    onClick={() => handleList(true)}
                                    disabled={listing}
                                />
                            </Stack>
                        )}
                    </Stack>
                </div>
            )}
        </Stack>
    );
};
